import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DirectionsRunRoundedIcon from '@mui/icons-material/DirectionsRunRounded';
import MobiledataOffRoundedIcon from '@mui/icons-material/MobiledataOffRounded';
import { CollateLogo, OpenMetadataLogo } from '../../icons/CollateIcons';
import { Link } from 'react-router-dom';
import { RELEASE_AUT_LANES, visibleLanes } from './autLanes';

let ops_sort = (a, b) => {
    if (a.start_date < b.start_date) {
        return 1;
    }
    if (a.start_date > b.start_date) {
        return -1;
    }
    return 0;
};

const AUTOutcomeSummary = (props) => {
    const { aut } = props;
    if (aut.outcome === undefined) {
        return <Typography variant="body2">No outcome for {JSON.stringify(aut)}</Typography>
    }
    return (
        <Typography variant="body2">
            <CheckRoundedIcon />{aut.outcome.expected}
            <CloseRoundedIcon />{aut.outcome.unexpected}
            <MobiledataOffRoundedIcon />{aut.outcome.flaky}
            <DirectionsRunRoundedIcon />{aut.outcome.skipped}
        </Typography>
    )
}

const AUTCell = ({ aut }) => {
    if (aut === undefined) {
        return <Typography variant="body2">No data</Typography>;
    }
    if (aut.status === 'completed') {
        return aut.outcome !== undefined && <AUTOutcomeSummary aut={aut} />;
    }
    return aut.status;
}

// Every AUT lane cell shares this; the icons sit on the text baseline.
const AUT_CELL_SX = { '& p svg': { verticalAlign: 'bottom' } };

export default function AUTList(props) {
    const { auts } = props;
    const [rows, setRows] = React.useState([]);


    React.useEffect(() => {
        setRows([...auts].reverse());
    }, [auts]);

    // Headers and cells are both derived from this, so they cannot drift apart.
    const lanes = visibleLanes(RELEASE_AUT_LANES, rows);

    if (rows.length === 0) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>No tests</div>
    }

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Number</TableCell>
                        <TableCell>Commits</TableCell>
                        {lanes.map(({ key, label }) => (
                            <TableCell key={key}>{label}</TableCell>
                        ))}
                        <TableCell>OMD Postgres</TableCell>
                        <TableCell>OMD MySQL</TableCell>
                        <TableCell>Test instance</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.sort(ops_sort).map((row, idx) => (
                        <TableRow
                            key={idx}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <TableCell component="th" scope="row">{auts.length - idx}</TableCell>
                            <TableCell sx={(theme) => (
                                {
                                    '& svg': {
                                        verticalAlign: 'bottom',
                                    }
                                }
                            )}>
                                {row.build_docker.status === 'errored' ?
                                    <Typography variant="body2">
                                        <Link to={`${row.build_docker.url}`}>Build failed</Link>
                                    </Typography>
                                :
                                <React.Fragment>
                                    <OpenMetadataLogo /> {row.build_docker.om_commit} / <CollateLogo />{row.build_docker.collate_commit}
                                </React.Fragment>
                            }
                            </TableCell>
                            {lanes.map(({ key }) => (
                                <TableCell key={key} sx={AUT_CELL_SX}>
                                    <AUTCell aut={row[key]} />
                                </TableCell>
                            ))}
                            <TableCell sx={(theme) => (
                                {
                                    '& p svg': {
                                        verticalAlign: 'bottom',
                                    }
                                }
                            )}>
                                {
                                    row.omd_postgresql === undefined ?
                                        <Typography variant="body2">No data</Typography>
                                        :
                                    row.omd_postgresql.status === 'completed' ? row.omd_postgresql.outcome !== undefined &&
                                        <AUTOutcomeSummary aut={row.omd_postgresql} />
                                        : row.omd_postgresql.status
                                }
                            </TableCell>
                            <TableCell sx={(theme) => (
                                {
                                    '& p svg': {
                                        verticalAlign: 'bottom',
                                    }
                                }
                            )}>
                                {
                                    row.omd_mysql === undefined ?
                                        <Typography variant="body2">No data</Typography>
                                        :
                                    row.omd_mysql.status === 'completed' ? row.omd_mysql.outcome !== undefined &&
                                        <AUTOutcomeSummary aut={row.omd_mysql} />
                                        : row.omd_mysql.status
                                }
                            </TableCell>
                            <TableCell>
                                {row.deploy_test === undefined ?
                                    <Typography variant="body2">No data</Typography>
                                    :
                                <Link to={row.deploy_test.url}>{row.deploy_test.status}</Link>
                                }
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
